// Shared data-fetch state machine for the admin console's filter/tab-keyed pages.
//
// Centralizes the loading / error / data + cancelled-flag pattern that was
// copy-pasted (and, in several pages, MISSING the cancelled flag) across the
// console. Fixes:
//   - ADM-5: rapid filter/tab toggling could let a slow earlier response clobber
//     the newer one (out-of-order render); the cancelled flag drops stale writes.
//   - ADM-6: a real loading state (vs. an empty table indistinguishable from
//     "no data") + a `refetch` for a Refresh/retry affordance.
//
// The fetch fn is re-invoked whenever `deps` change (same contract as a
// useEffect dep array) or `refetch()` is called. `setData` is exposed so a page
// can append (load-more) or optimistically patch without a full refetch.
import { useCallback, useEffect, useState, type Dispatch, type SetStateAction } from "react";
import { errMsg } from "../lib/adminApi";

export interface AdminQuery<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
  setData: Dispatch<SetStateAction<T | null>>;
}

export function useAdminQuery<T>(
  fn: () => Promise<T>,
  deps: unknown[],
): AdminQuery<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  const refetch = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    let cancelled = false;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- pre-request loading/error reset when the request key changes; results are only written after the await, guarded by the `cancelled` flag
    setLoading(true);
    setError(null);
    fn()
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((e) => {
        if (!cancelled) setError(errMsg(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // fn is intentionally excluded — callers pass an inline closure; `deps` is the
    // explicit re-run key (mirrors useEffect's dep-array contract). `nonce` drives
    // refetch().
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce]);

  return { data, loading, error, refetch, setData };
}
